import * as postRobot from 'post-robot';
export const IFRAME_PANEL_CSS_CLASS = 'pipefy-ai-iframe__panel';

const IFRAME_PANEL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@500');

.${IFRAME_PANEL_CSS_CLASS} {
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 100%;
  max-width: 480px;
  height: calc(100vh - 64px);
  background-color: white;
  border: 1px solid rgba(237, 239, 243, 1);
  border-radius: 8px;
  box-shadow: 0px 24px 24px 0px rgba(38, 50, 56, 0.08),
    0px 32px 32px -40px rgba(38, 50, 56, 0.14),
    0px 12px 16px 0px rgba(0, 0, 0, 0.08);
  color-scheme: normal;
  opacity: 1;
  transform: translateY(0);
  transition: visibility 0.125s, opacity 0.125s, transform 0.3s, max-width 0.3s, width 0.3s, height 0.3s;
  will-change: visibility, opacity, transform;
  z-index: 10000 !important;
}


.${IFRAME_PANEL_CSS_CLASS}--expanded {
  max-width: calc(100vw - 32px);
  height: calc(100vh - 32px);
  }
  
  .${IFRAME_PANEL_CSS_CLASS}--hidden {
  visibility: hidden;
  opacity: 0;
  transform: translateY(10%);
}

  @media (max-width: 520px) {
    .${IFRAME_PANEL_CSS_CLASS}, .${IFRAME_PANEL_CSS_CLASS}--expanded {
      height: 100%;
      width: 100%;
      bottom: 0;
      right: 0;
      max-width: 100%;
    }
  }
`;

interface IframeControllerProps {
  id: string;
  src: string;
  parent?: HTMLElement;
}

export class IframeController<Events extends { type: string }> {
  id: string;
  iframe: HTMLIFrameElement;
  isVisible: boolean;
  isExpanded: boolean;
  domain: string;

  constructor({ id, parent, src }: IframeControllerProps) {
    this.id = id;
    this.isVisible = false;
    this.isExpanded = false;

    const style = document.createElement('style');
    style.innerHTML = IFRAME_PANEL_CSS;
    document.head.appendChild(style);

    this.iframe = document.createElement('iframe');
    this.iframe.setAttribute('data-testid', id);
    this.iframe.setAttribute('allow', 'clipboard-read; clipboard-write');
    this.iframe.id = this.id;
    this.iframe.src = src;
    this.domain = new URL(src).origin;

    this.iframe.classList.add(IFRAME_PANEL_CSS_CLASS);
    this.addClassModifier('hidden');

    this.inject(parent);
    this.addBaseTag();
  }

  private addBaseTag() {
    this.iframe.addEventListener('load', () => {
      if (this.iframe.contentDocument) {
        const base = this.iframe.contentDocument.createElement('base');
        base.target = '_blank';
        this.iframe.contentDocument.head.appendChild(base);
      }
    });
  }

  private inject(parent?: HTMLElement) {
    if (document.getElementById(this.id)) {
      console.warn(`Iframe with id "${this.id}" already exists.`);
      return;
    }

    if (parent) {
      parent.appendChild(this.iframe);
    } else {
      document.body.appendChild(this.iframe);
    }
  }

  private addClassModifier(modifier: string) {
    this.iframe.classList.add(`${IFRAME_PANEL_CSS_CLASS}--${modifier}`);
  }

  private removeClassModifier(modifier: string) {
    this.iframe.classList.remove(`${IFRAME_PANEL_CSS_CLASS}--${modifier}`);
  }

  show() {
    this.isVisible = true;
    this.removeClassModifier('hidden');
  }

  hide() {
    this.isVisible = false;
    this.addClassModifier('hidden');
  }

  expand() {
    this.isExpanded = true;
    this.addClassModifier('expanded');
  }

  collapse() {
    this.isExpanded = false;
    this.removeClassModifier('expanded');
  }

  sendPostMessage(event: Events): void {
    const { type, ...data } = event;
    if (this.iframe.contentWindow) {
      postRobot.send(this.iframe.contentWindow, type, data, {
        domain: this.domain,
      });
    }
  }

  onEvent<T extends Events['type']>(
    type: T,
    callback?: (data: {
      data: Omit<Extract<Events, { type: T }>, 'type'>;
    }) => Promise<void | object>
  ): void {
    postRobot.on(
      type,
      {
        window: this.iframe.contentWindow,
        domain: this.domain,
      },
      callback
    );
  }
}
